import {
  EntityComparator,
  EntityCriteriaOperator,
  EntityOperator,
  EntityValueType,
  IEntityCriteriaSimpleOperator,
  IEntitySelectionCriteria,
  ITieredEntitySelection,
  ITieredEntitySelectionCriterion,
  IEntityCriteriaCompoundOperator,
} from '../../../../common/types/shared/entitySelection';
import { v4 as uuIdV4 } from 'uuid';
import {
  EntityCriteriaCompoundOperatorInput,
  EntitySelectionCriteriaInput,
  RelatedEntityConfigInput,
} from '../../../../types/generated-types';

export const defaultTieredEntitySelection = (
  modelTypeName: string = 'Thermostat',
): ITieredEntitySelection => ({
  id: uuIdV4(),
  targetModel: {
    modelName: modelTypeName,
  },
  basisName: 'Device',
  selectionCriteria: defaultCompoundOperator(),
});

export const defaultEntityOperator: EntityOperator = EntityOperator.AND;

export const defaultIEntitySelectionCriterion: () => EntitySelectionCriteriaInput =
  () => ({
    criteria: [],
    simpleOperators: [],
    compoundOperators: [],
  });

export const defaultCriterion: () => ITieredEntitySelectionCriterion = () => ({
  id: uuIdV4(),
  selectionPath: '',
  comparator: EntityComparator.EQUAL_TO,
  valueType: EntityValueType.STRING,
  values: [],
  rangeConfig: undefined,
});

export const defaultSimpleOperator: () => IEntityCriteriaSimpleOperator =
  () => ({
    id: uuIdV4(),
    operator: EntityCriteriaOperator.AND,
    criteria: [],
  });

export const defaultCompoundOperator: () => IEntityCriteriaCompoundOperator =
  () => ({
    id: uuIdV4(),
    operator: EntityCriteriaOperator.AND,
    criteria: {
      criteria: [],
      simpleOperators: [],
      compoundOperators: [],
    },
  });

export const cloneCriterion = (
  criterion: ITieredEntitySelectionCriterion,
): ITieredEntitySelectionCriterion => {
  return {
    ...criterion,
    values: criterion.values ? [...criterion.values] : criterion.values,
  };
};

export const cloneSimpleOperator = (
  operator: IEntityCriteriaSimpleOperator,
): IEntityCriteriaSimpleOperator => {
  return {
    ...operator,
    criteria: operator.criteria.map((c) => cloneCriterion(c)),
  };
};

export const cloneCompoundOperator = (
  operator: IEntityCriteriaCompoundOperator,
): IEntityCriteriaCompoundOperator => {
  return {
    ...operator,
    criteria: {
      criteria: operator.criteria.criteria.map((c) => cloneCriterion(c)),
      simpleOperators: operator.criteria.simpleOperators?.map((o) =>
        cloneSimpleOperator(o),
      ),
      compoundOperators: operator.criteria.compoundOperators?.map((o) =>
        cloneCompoundOperator(o),
      ),
    },
  };
};

export const cloneEntitySelectionCriteria = (
  spec: IEntitySelectionCriteria,
) => {
  return {
    criteria: spec.criteria.map((c) => cloneCriterion(c)),
    simpleOperators: spec.simpleOperators
      ? spec.simpleOperators.map((c) => cloneSimpleOperator(c))
      : undefined,
    compoundOperators: spec.compoundOperators
      ? spec.compoundOperators.map((c) => cloneCompoundOperator(c))
      : undefined,
  };
};

export const cloneTieredEntitySelection = (
  value: ITieredEntitySelection,
): ITieredEntitySelection => {
  return {
    ...value,
    targetModel: {
      ...value.targetModel,
    },
    selectionCriteria: {
      ...value.selectionCriteria,
      criteria: cloneEntitySelectionCriteria(value.selectionCriteria.criteria),
    },
  };
};

export function cloneRelatedEntityFilter(
  suppliedRelatedEntityFilter: RelatedEntityConfigInput,
) {
  const newRelatedEntityFilter: RelatedEntityConfigInput = {
    relatedEntities: suppliedRelatedEntityFilter.relatedEntities
      ? suppliedRelatedEntityFilter.relatedEntities.map((filter) =>
          filter ? cloneRelatedEntityFilter(filter) : filter,
        )
      : undefined,
    relationshipName: suppliedRelatedEntityFilter.relationshipName,
    selectionCriteria: suppliedRelatedEntityFilter.selectionCriteria
      ? cloneCompoundOperator(suppliedRelatedEntityFilter.selectionCriteria)
      : suppliedRelatedEntityFilter.selectionCriteria,
  };
  return newRelatedEntityFilter;
}

export function isValidSelectionCriteria(
  candidate: EntityCriteriaCompoundOperatorInput | null | undefined,
) {
  if (!candidate) {
    return false;
  }

  if (!candidate.operator || !candidate.id) {
    return false;
  }

  const criteria = candidate.criteria;
  if (!criteria) {
    return false;
  }

  const validCriteria = criteria.criteria?.length > 0;
  const validSimpleOperators = (criteria.simpleOperators ?? []).length > 0;
  const validCompoundOperators = (criteria.compoundOperators ?? []).length > 0;

  return validCriteria || validSimpleOperators || validCompoundOperators;
}
